import React, { useState } from 'react';
import { 
  BuildingOfficeIcon, 
  UsersIcon, 
  GlobeAltIcon,
  Cog6ToothIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import MultiSiteManagement from '../components/administration/MultiSiteManagement';
import TenantManagement from '../components/administration/TenantManagement';

const MultiSiteTenantPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    {
      id: 'overview',
      name: 'Vue d\'ensemble',
      icon: ChartBarIcon,
      description: 'Tableau de bord et statistiques multi-site'
    },
    {
      id: 'sites',
      name: 'Gestion des sites',
      icon: BuildingOfficeIcon,
      description: 'Créer, modifier et gérer les sites'
    },
    {
      id: 'tenants',
      name: 'Gestion des tenants',
      icon: GlobeAltIcon,
      description: 'Gérer les organisations et entreprises'
    },
    {
      id: 'users',
      name: 'Attribution utilisateurs',
      icon: UsersIcon,
      description: 'Affecter les utilisateurs aux sites'
    },
    {
      id: 'settings',
      name: 'Paramètres',
      icon: Cog6ToothIcon,
      description: 'Configuration multi-site et permissions'
    }
  ];

  const handleQuickAction = (tabId: string) => {
    setActiveTab(tabId);
    
    // Feedback visuel : mettre en surbrillance l'onglet sélectionné
    const tabElement = document.querySelector(`[data-tab="${tabId}"]`);
    if (tabElement) {
      tabElement.classList.add('ring-2', 'ring-orange-500', 'ring-opacity-50');
      setTimeout(() => {
        tabElement.classList.remove('ring-2', 'ring-orange-500', 'ring-opacity-50');
      }, 2000);
    }
    
    // Scroll vers le haut de la page pour voir le contenu
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Notification visuelle
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform duration-300';
    notification.textContent = `Navigation vers ${tabs.find(tab => tab.id === tabId)?.name}`;
    document.body.appendChild(notification);
    
    // Animer l'entrée
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Supprimer après 3 secondes
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab onQuickAction={handleQuickAction} />;
      case 'sites':
        return <MultiSiteManagement />;
      case 'tenants':
        return <TenantManagement onTenantsUpdate={() => {}} />;
      case 'users':
        return <MultiSiteManagement />;
      case 'settings':
        return <SettingsTab />;
      default:
        return <OverviewTab onQuickAction={handleQuickAction} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                  <GlobeAltIcon className="w-8 h-8 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  Multi-Site & Tenant
                </h1>
                <p className="text-sm text-gray-600">
                  Gestion centralisée des sites, tenants et utilisateurs
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-tab={tab.id}
              >
                <div className="flex items-center space-x-2">
                  <tab.icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>
    </div>
  );
};

// Composant Vue d'ensemble
interface OverviewTabProps {
  onQuickAction: (tabId: string) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ onQuickAction }) => {
  return (
    <div className="space-y-6">
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                <GlobeAltIcon className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Tenants</p>
              <p className="text-2xl font-semibold text-gray-900">12</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                <BuildingOfficeIcon className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sites</p>
              <p className="text-2xl font-semibold text-gray-900">48</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                <UsersIcon className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Utilisateurs Actifs</p>
              <p className="text-2xl font-semibold text-gray-900">156</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                <Cog6ToothIcon className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Sites Bloqués</p>
              <p className="text-2xl font-semibold text-gray-900">2</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Actions rapides</h3>
          <p className="text-sm text-gray-600 mt-1">Cliquez sur une action pour accéder directement à la section correspondante</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              onClick={() => onQuickAction('sites')}
              className="quick-action-button blue"
            >
              <div className="quick-action-icon bg-blue-100">
                <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="quick-action-content">
                <p className="quick-action-title">Créer un site</p>
                <p className="quick-action-description">Ajouter un nouveau site</p>
              </div>
              <div className="quick-action-indicator">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              </div>
            </button>

            <button 
              onClick={() => onQuickAction('tenants')}
              className="quick-action-button green"
            >
              <div className="quick-action-icon bg-green-100">
                <GlobeAltIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="quick-action-content">
                <p className="quick-action-title">Créer un tenant</p>
                <p className="quick-action-description">Ajouter une organisation</p>
              </div>
              <div className="quick-action-indicator">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
            </button>

            <button 
              onClick={() => onQuickAction('users')}
              className="quick-action-button purple"
            >
              <div className="quick-action-icon bg-purple-100">
                <UsersIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="quick-action-content">
                <p className="quick-action-title">Affecter utilisateur</p>
                <p className="quick-action-description">Gérer les permissions</p>
              </div>
              <div className="quick-action-indicator">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Activité récente */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Activité récente</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-sm text-gray-600">Nouveau site créé: "Site Abidjan"</span>
              <span className="text-xs text-gray-400">Il y a 2 heures</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-sm text-gray-600">Tenant "CITA" mis à jour</span>
              <span className="text-xs text-gray-400">Il y a 4 heures</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-red-400 rounded-full"></div>
              <span className="text-sm text-gray-600">Site "Bouaké" bloqué - Maintenance</span>
              <span className="text-xs text-gray-400">Il y a 1 jour</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <span className="text-sm text-gray-600">Utilisateur "admin" affecté au site "Abidjan"</span>
              <span className="text-xs text-gray-400">Il y a 2 jours</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant Paramètres
const SettingsTab: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Paramètres Multi-Site</h3>
        </div>
        <div className="p-6 space-y-6">
          {/* Configuration générale */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Configuration générale</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Site par défaut
                </label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option>Abidjan (ABJ)</option>
                  <option>Bouaké (BOU)</option>
                  <option>San Pedro (SP)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fuseau horaire par défaut
                </label>
                <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option>Africa/Abidjan</option>
                  <option>UTC</option>
                </select>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Permissions</h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <input type="checkbox" id="create_sites" className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label htmlFor="create_sites" className="ml-2 text-sm text-gray-700">
                  Permettre la création de sites
                </label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="create_tenants" className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label htmlFor="create_tenants" className="ml-2 text-sm text-gray-700">
                  Permettre la création de tenants
                </label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="block_sites" className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label htmlFor="block_sites" className="ml-2 text-sm text-gray-700">
                  Permettre le blocage des sites
                </label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="block_tenants" className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label htmlFor="block_tenants" className="ml-2 text-sm text-gray-700">
                  Permettre le blocage des tenants
                </label>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Notifications</h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <input type="checkbox" id="notify_site_creation" className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label htmlFor="notify_site_creation" className="ml-2 text-sm text-gray-700">
                  Notifier lors de la création d'un site
                </label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="notify_site_blocking" className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label htmlFor="notify_site_blocking" className="ml-2 text-sm text-gray-700">
                  Notifier lors du blocage d'un site
                </label>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="notify_tenant_changes" className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <label htmlFor="notify_tenant_changes" className="ml-2 text-sm text-gray-700">
                  Notifier les changements de tenants
                </label>
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex space-x-3 pt-4">
            <button className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors">
              Sauvegarder les paramètres
            </button>
            <button className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors">
              Réinitialiser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiSiteTenantPage;
